import { useCallback, useEffect, useMemo } from 'react'

import { useBlueprintEditorContext } from '@editor/pages/projects/blueprint-editor/contexts/BlueprintEditorContext'
import { Mode } from '@editor/pages/projects/blueprint-editor/definitions'
import { setInitCompleted } from '@editor/pages/projects/blueprint-editor/store/blueprintEditor'
import { computeOrthoMinZoomToFit } from '@editor/services/Camera'
import { ArcballControls } from '@editor/services/threejs'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useThree } from '@react-three/fiber'
import { useSelector } from 'react-redux'
import { Box3, Vector3 } from 'three'

import useConfigureCanvas from '../hooks/useConfigureCanvas'

/**
 * Camera manager for handling camera-related logic.
 */
const CameraManager = () => {
  // Hooks
  const { set } = useThree()
  const bpEditorContext = useBlueprintEditorContext()
  const { pointClouds, pointCloud2ds, sceneRef, perspectiveCamera, orthographicCamera, activeCamera, arcballControls } =
    bpEditorContext
  const { setupControls, orientCamera } = useConfigureCanvas(bpEditorContext)

  // Store
  const dispatch = useAppDispatch()
  const isDragging = useSelector((state: RootState) => state.toolUtil.core.isDragging)
  const mode = useSelector((state: RootState) => state.blueprint.editor.mode)
  const initCompleted = useSelector((state: RootState) => state.blueprint.editor.initCompleted)
  const pcdBoundingSphereRadius = useSelector((state: RootState) => state.blueprint.editor.pcdBoundingSphereRadius)
  const pcdBoundingBox = useSelector((state: RootState) => state.blueprint.editor.pcdBoundingBox)

  // Derived
  const bbox = useMemo(
    () =>
      pcdBoundingBox ? new Box3(new Vector3(...pcdBoundingBox[0]), new Vector3(...pcdBoundingBox[1])) : new Box3(),
    [pcdBoundingBox],
  )
  const bboxCenter = useMemo(() => bbox.getCenter(new Vector3()), [bbox])

  /**
   * Handle resizing of the canvas and update camera projection matrices.
   * This ensures the cameras are correctly set up for the current viewport size.
   */
  const handleResize = useCallback(() => {
    const canvas = document.getElementById('canvasBox')
    if (!canvas || !arcballControls.current) return

    const aspect = canvas.clientWidth / canvas.clientHeight
    const orthoWidth =
      canvas.clientWidth < canvas.clientHeight ? pcdBoundingSphereRadius * aspect : pcdBoundingSphereRadius
    const orthoHeight =
      canvas.clientHeight < canvas.clientWidth ? pcdBoundingSphereRadius / aspect : pcdBoundingSphereRadius

    orthographicCamera.current.left = -orthoWidth
    orthographicCamera.current.right = orthoWidth
    orthographicCamera.current.top = orthoHeight
    orthographicCamera.current.bottom = -orthoHeight
    orthographicCamera.current.far = pcdBoundingSphereRadius * 2
    orthographicCamera.current.updateProjectionMatrix()

    perspectiveCamera.current.aspect = aspect
    perspectiveCamera.current.updateProjectionMatrix()

    arcballControls.current.minZoom = computeOrthoMinZoomToFit(bbox, orthographicCamera.current, 2)
  }, [perspectiveCamera, orthographicCamera, arcballControls, pcdBoundingSphereRadius, bbox])

  /**
   * Initialize the scene with cameras and controls.
   * This runs only once when the component mounts.
   */
  useEffect(() => {
    if (arcballControls?.current || !sceneRef.current || !pointClouds.length || initCompleted) return

    const canvas = document.getElementById('canvasBox')
    if (!canvas) return

    // Set orthographic camera
    const center = bboxCenter

    const orthoCameraDirection = new Vector3()
    orthographicCamera.current.getWorldDirection(orthoCameraDirection)
    const orthoOffset = orthoCameraDirection.clone().multiplyScalar(-pcdBoundingSphereRadius)
    orthographicCamera.current.position.add(orthoOffset)
    orthographicCamera.current.updateProjectionMatrix()

    sceneRef.current.add(orthographicCamera.current)

    // Set perspective camera
    perspectiveCamera.current.position.set(center.x || 0, (center.y || 0) - pcdBoundingSphereRadius, center.z || 0)
    perspectiveCamera.current.up.set(0, 0, 1)
    perspectiveCamera.current.updateProjectionMatrix()

    sceneRef.current.add(perspectiveCamera.current)

    // Set controls
    if (arcballControls) {
      const controls = new ArcballControls(activeCamera.current, canvas, sceneRef.current)
      controls.enableAnimations = false
      controls.enableFocus = false // We want finer control over focus. It'll be handled by the CameraFocus tool.
      controls.maxDistance = pcdBoundingSphereRadius * 3 // for PerspectiveCamera
      controls.maxZoom = pcdBoundingSphereRadius * 4 // for OrthographicCamera
      controls.activateGizmos(false)
      controls.setGizmosVisible(false)

      arcballControls.current = controls
    }

    // Set camera's viewport
    handleResize()
    dispatch(setInitCompleted(true))
  }, [
    sceneRef,
    pointClouds,
    activeCamera,
    bboxCenter,
    pcdBoundingSphereRadius,
    arcballControls,
    initCompleted,
    perspectiveCamera,
    orthographicCamera,
    handleResize,
    dispatch,
  ])

  useEffect(() => {
    if (!arcballControls.current) return

    arcballControls.current.enabled = !isDragging
  }, [isDragging, arcballControls])

  /**
   * Change camera when changing modes.
   */
  useEffect(() => {
    if (!perspectiveCamera.current || !orthographicCamera.current || !initCompleted || !pointClouds.length) return

    if (mode === Mode.Editor3D) {
      set({
        camera: perspectiveCamera.current,
      })
    } else {
      set({
        camera: orthographicCamera.current,
      })
    }

    setupControls(mode)
    orientCamera(mode)
  }, [
    mode,
    perspectiveCamera,
    orthographicCamera,
    initCompleted,
    pointClouds,
    pointCloud2ds,
    set,
    setupControls,
    orientCamera,
  ])

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [handleResize])

  return null
}

export default CameraManager
